import "./style.scss";

import React, { FC, useEffect, useRef, useState } from "react";
import { ReactComponent as IconSearch } from "assets/img/icons/icon_Search.svg";
import { ReactComponent as IconChevronDown } from "assets/img/icons/icon_carret-Down.svg";
import { ReactComponent as IconChevronUp } from "assets/img/icons/icon_carret-Up.svg";
import { ReactComponent as IconClose } from "assets/img/icons/icon_Close.svg";

import countryCode from "assets/data/CountryCodes.json";

interface SelectCountryProps {
    country: string;
    field: any;
    phone?: boolean;
    disabled?: boolean;
}

const SelectCountry: FC<SelectCountryProps> = ({
                                                   field: { name, value, onChange, onBlur },
                                                   phone = false,
                                                   disabled = false,
                                               }) => {
    const selectRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const [isOpened, setIsOpened] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [filteredOptions, setFilteredOptions] = useState(countryCode);
    const [isInitialized, setIsInitialized] = useState(false);

    // Trouver la France dans la liste
    const france = countryCode.find(c => c.code === "FR");

    // Définir la valeur par défaut à la France si aucune valeur n'est sélectionnée
    useEffect(() => {
        // Only set default value once when component mounts and no value exists
        if (!isInitialized && !value && france && !disabled) {
            const defaultValue = phone ? france.dial_code : france.name;
            onChange({
                target: {
                    name,
                    value: defaultValue,
                },
            });
            setIsInitialized(true);
        }
    }, [value, phone, name, onChange, france, disabled, isInitialized]);

    // Filter options based on search term
    useEffect(() => {
        if (searchTerm.trim() === "") {
            setFilteredOptions(countryCode);
        } else {
            const filtered = countryCode.filter(country => {
                const searchLower = searchTerm.toLowerCase();
                return (
                  country.name.toLowerCase().includes(searchLower) ||
                  country.code.toLowerCase().includes(searchLower) ||
                  (phone && country.dial_code.includes(searchTerm))
                );
            });
            setFilteredOptions(filtered);
        }
    }, [searchTerm, phone]);

    const handleOptionSelect = (optionValue: string) => {
        setIsOpened(false);
        setSearchTerm("");
        onChange({
            target: {
                name,
                value: optionValue,
            },
        });
        if (onBlur) onBlur({ target: { name } });
    };

    const handleSelectToggle = () => {
        if (!disabled) {
            setIsOpened(!isOpened);
            setSearchTerm("");
            // Focus search input when opened
            if (!isOpened) {
                setTimeout(() => {
                    searchInputRef.current?.focus();
                }, 100);
            }
        }
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
            setIsOpened(false);
            setSearchTerm("");
        }
    };

    const handleClearSearch = () => {
        setSearchTerm("");
        searchInputRef.current?.focus();
    };

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Find selected country based on current value
    const selectedCountry = countryCode.find((c) =>
      phone ? c.dial_code === value : c.name === value
    );

    // Get display value for the trigger
    const getTriggerDisplay = () => {
        if (!selectedCountry) {
            return phone ? "Sélectionner un indicatif" : "Sélectionner un pays";
        }
        return phone
          ? `${selectedCountry.dial_code}`
          : selectedCountry.name;
    };

    return (
      <div
        className={`SelectCountry select-wrapper ${disabled ? "disabled" : ""}`}
        ref={selectRef}
      >
          <select
            className="select"
            value={value || ""}
            name={name}
            onChange={onChange}
            onBlur={onBlur}
            style={{ display: "none" }}
            disabled={disabled}
          />
          <div className={`custom-select ${isOpened ? "opened" : ""}`}>
              <div
                className={`custom-select__trigger ${disabled ? "disabled" : ""}`}
                onClick={handleSelectToggle}
              >
                    <span className="d-flex align-items-center">
                        {selectedCountry && (
                          <img
                            src={`${process.env.PUBLIC_URL}/img/flags/${selectedCountry.code.toLowerCase()}.png`}
                            alt={selectedCountry.name}
                            onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        )}
                        <span className="trigger-text">{getTriggerDisplay()}</span>
                    </span>
                  <span className="trigger-icon">
                        {isOpened ? <IconChevronUp /> : <IconChevronDown />}
                    </span>
              </div>

              {isOpened && (
                <div className="custom-options">
                    {/* Search Input */}
                    <div className="search-container">
                        <div className="search-input-wrapper">
                            <IconSearch className="search-icon" />
                            <input
                              ref={searchInputRef}
                              type="text"
                              className="search-input"
                              placeholder={phone ? "Rechercher un indicatif..." : "Rechercher un pays..."}
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            {searchTerm && (
                              <IconClose
                                className="clear-icon"
                                onClick={handleClearSearch}
                              />
                            )}
                        </div>
                    </div>

                    {/* Options List */}
                    <div className="options-list">
                        {filteredOptions.length > 0 ? (
                          filteredOptions.map(({ code, name, dial_code }, index) => (
                            <span
                              className={`custom-option ${
                                (phone ? dial_code : name) === value ? "selected" : ""
                              }`}
                              data-value={phone ? dial_code : name}
                              key={index}
                              onClick={() => handleOptionSelect(phone ? dial_code : name)}
                            >
                                        <img
                                          src={`${process.env.PUBLIC_URL}/img/flags/${code.toLowerCase()}.png`}
                                          alt={name}
                                          onError={(e) => {
                                              (e.target as HTMLImageElement).style.display = 'none';
                                          }}
                                        />
                                        <span className="option-text">
                                            {name}
                                            {phone && <span className="dial-code">{dial_code}</span>}
                                        </span>
                                {(phone ? dial_code : name) === value && (
                                  <span className="check-mark">✓</span>
                                )}
                                    </span>
                          ))
                        ) : (
                          <div className="no-results">
                              Aucun résultat trouvé
                          </div>
                        )}
                    </div>
                </div>
              )}
          </div>
      </div>
    );
};

export default SelectCountry;